// src/components/Produccion.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  Typography,
  Spin,
  Modal,
  Descriptions,
  Select,
  Button,
  message
} from 'antd';
import { FaMusic, FaPlay } from 'react-icons/fa';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { db } from '../config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  documentId,
  updateDoc,
  doc
} from 'firebase/firestore';

dayjs.extend(relativeTime);
const { Title, Text } = Typography;

const productionStates = [
  { value: 'Sin producir', label: 'Sin producir' },
  { value: 'En Producción', label: 'En Producción' },
  { value: 'Canción Enviada', label: 'Canción Enviada' }
];

const API = process.env.REACT_APP_API_URL;

export default function Produccion() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [letras, setLetras] = useState([]);
  const [loadingLetras, setLoadingLetras] = useState(false);

  // 1) Carga leads con estado "Compró"
  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'leads'),
      where('estado', '==', 'Compró'),
      orderBy('fecha_creacion', 'desc')
    );
    const unsub = onSnapshot(
      q,
      snap => {
        setLeads(snap.docs.map(d => ({ id: d.id, key: d.id, ...d.data() })));
        setLoading(false);
      },
      err => {
        console.error('Error cargando leads:', err);
        message.error('No se pudieron cargar los leads');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 2) Actualizar estadoProduccion manualmente
  const handleEstadoChange = async (leadId, newState) => {
    try {
      await updateDoc(doc(db, 'leads', leadId), { estadoProduccion: newState });
      setLeads(ls =>
        ls.map(l => (l.id === leadId ? { ...l, estadoProduccion: newState } : l))
      );
      message.success('Estado de producción actualizado');
    } catch (err) {
      console.error(err);
      message.error('Error actualizando estado');
    }
  };

  // 3) Mostrar letras en modal
  const fetchLetras = async record => {
    setSelectedLead(record);
    setLoadingLetras(true);
    try {
      const ids = record.letraIds || [];
      const data = [];
      for (let i = 0; i < ids.length; i += 10) {
        const snap = await getDocs(
          query(collection(db, 'letras'), where(documentId(), 'in', ids.slice(i, i + 10)))
        );
        snap.docs.forEach(d => data.push({ id: d.id, ...d.data() }));
      }
      setLetras(data);
      setModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error('Error cargando letras');
    } finally {
      setLoadingLetras(false);
    }
  };

  // 4) Enviar canción completa como documento adjunto
  const handleSendFull = async record => {
    try {
      const resp = await fetch(`${API}/api/whatsapp/send-full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: record.id })
      });
      if (!resp.ok) {
        const text = await resp.text();
        console.error('Error enviando completa:', resp.status, text);
        return message.error(`Error ${resp.status}`);
      }
      // Actualizar en Firestore el estadoProduccion
      await updateDoc(doc(db, 'leads', record.id), { estadoProduccion: 'Canción Enviada' });
      setLeads(ls =>
        ls.map(l =>
          l.id === record.id ? { ...l, estadoProduccion: 'Canción Enviada' } : l
        )
      );
      message.success('Te comparto tu canción completa');
    } catch (err) {
      console.error('Error de red al enviar completa:', err);
      message.error('Error de red al enviar canción completa');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      render: t => t || '-'
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      render: t => t || '-'
    },
    {
      title: 'Fecha de compra',
      dataIndex: 'fecha_creacion',
      key: 'fecha_creacion',
      render: v => (v ? dayjs(v.toDate ? v.toDate() : v).fromNow() : '-')
    },
    {
      title: 'Estado Prod.',
      dataIndex: 'estadoProduccion',
      key: 'estadoProduccion',
      render: (val, r) => (
        <Select
          value={val || 'Sin producir'}
          options={productionStates}
          onChange={s => handleEstadoChange(r.id, s)}
          style={{ width: 160 }}
        />
      )
    },
    {
      title: 'Detalles',
      key: 'detalles',
      render: (_, r) => (
        <Button type="link" onClick={() => fetchLetras(r)}>
          Ver detalles
        </Button>
      )
    },
    {
      title: 'Enviar canción completa',
      key: 'sendFull',
      render: (_, r) => (
        <Button
          type="primary"
          icon={<FaPlay />}
          onClick={() => handleSendFull(r)}
        >
          Enviar completa
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <FaMusic style={{ fontSize: 28, color: '#1890ff' }} />
        <Title level={2} style={{ margin: 0, marginLeft: 12 }}>
          Producción
        </Title>
      </div>

      {loading ? (
        <Spin tip="Cargando producciones..." />
      ) : (
        <Table
          dataSource={leads}
          columns={columns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No hay leads con estado Compró' }}
          rowKey="id"
        />
      )}

      <Modal
        visible={modalVisible}
        title={selectedLead ? `Detalle de ${selectedLead.nombre}` : ''}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={700}
      >
        {loadingLetras ? (
          <Spin tip="Cargando letras..." />
        ) : letras.length ? (
          letras.map(l => {
            const match = l.letra.match(/\*\*(.*?)\*\*/);
            const titulo = match ? match[1] : 'Sin título';
            const texto = l.letra.replace(/\*\*(.*?)\*\*/, '').trim();
            return (
              <Descriptions
                key={l.id}
                column={1}
                bordered
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="Título">{titulo}</Descriptions.Item>
                <Descriptions.Item label="Artista">{l.artist || '-'}</Descriptions.Item>
                <Descriptions.Item label="Género">{l.genre || '-'}</Descriptions.Item>
                <Descriptions.Item label="Voz">{l.voiceType || '-'}</Descriptions.Item>
                <Descriptions.Item label="Estado">{l.status || '-'}</Descriptions.Item>
                <Descriptions.Item label="Letra">
                  <Text>{texto || '-'}</Text>
                </Descriptions.Item>
              </Descriptions>
            );
          })
        ) : (
          <Text>No hay letras para este lead.</Text>
        )}
      </Modal>
    </div>
  );
}
